import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSnackbar } from '@context/SnackbarContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import {
  TradingAccountRebateDetailedDto,
  hasGroupRebateSettings,
  hasIndividualRebateSettings,
} from '@model/TradingAccountRebateDetailedDto'

interface ResetRebateSettings {
  isLoading: boolean
  resetRebateSettings: (client: TradingAccountRebateDetailedDto) => void
}

interface ResetRebateSettingsParams {
  onCompleted?: () => void
}

export const useResetRebateSettings = ({
  onCompleted,
}: ResetRebateSettingsParams = {}): ResetRebateSettings => {
  const apiClient = useApiClient(ClientApiClient)
  const { addSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const { account } = useAccountReadContext()

  const [isLoading, setIsLoading] = useState(false)

  const resetRebateSettings = async (client: TradingAccountRebateDetailedDto) => {
    if (!account?.clientIntroducingBroker?.id) {
      return
    }

    setIsLoading(true)

    try {
      if (hasGroupRebateSettings(client) && client.rebateGroup) {
        await apiClient.deleteIBTradingAccountsRebateGroups({
          introducingBrokerId: account.clientIntroducingBroker.id,
          tradingAccountId: client.tradingAccount.id,
          groupId: client.rebateGroup.id,
        })
      }

      if (hasIndividualRebateSettings(client)) {
        await apiClient.deleteIBTradingAccountsRebates({
          introducingBrokerId: account.clientIntroducingBroker.id,
          tradingAccountId: client.tradingAccount.id,
        })
      }

      addSnackbar.success({
        message: t('IB.RebatesPage.ClientsPage.You have successfully reset the rebate settings'),
      })

      setIsLoading(false)
      onCompleted?.()
    } catch (error: unknown) {
      setIsLoading(false)
    }
  }

  return { resetRebateSettings, isLoading }
}
